import React, { FC } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import { AppIntegrationDetails, IntegrationDetails } from "models";

import { INTEGRATION_CATALOG } from "../integrationCatalog";

import style from "./style.scss";

export interface IntegrationDetailsDialogProps {
    /** The integration to show; nothing is shown without one */
    integrationId?: string;
    data?: AppIntegrationDetails;
    onClose(): void;
}

const onOff = (value?: boolean | null) => (value ? "On" : "Off");

/**
 * What we know about a connected integration. Only what the integrationStatus query already
 * returns, never the stored token: `integration(type:)` can return raw credentials, and those must
 * not reach the screen.
 */
export function integrationDetails(integrationId: string, data: AppIntegrationDetails = {}): [string, string][] {
    if (integrationId === "searchlight") {
        const feed = data.searchLightFeed;
        return [
            ["Status", feed?.enabled ? "Feed on" : "Feed off"],
            ...(feed?.accountId ? ([["SearchLight account", feed.accountId]] as [string, string][]) : []),
        ];
    }

    const integration = (data as Record<string, IntegrationDetails | undefined>)[integrationId];
    const rows: [string, string][] = [
        ["Status", integration?.isActive ? "Connected and working" : "Needs attention: reconnect it"],
    ];

    switch (integrationId) {
        case "servicetitan": {
            const st = data.servicetitan;
            rows.push(["Lead import", onOff(st?.leadImport)]);
            rows.push(["Job outcome tracking", onOff(st?.outcomeTracking)]);
            rows.push([
                "Invoice access",
                st?.invoicesRead === true ? "Granted" : st?.invoicesRead === false ? "Not granted: upgrade the XAPP app in ServiceTitan" : "Not checked yet",
            ]);
            break;
        }
        case "serviceminder":
            rows.push(["Live availability", onOff(data.serviceminder?.liveAvailability)]);
            break;
        case "webhook":
            rows.push(["Payload", data.webhook?.flattenPayload ? "Flat (single level)" : "Nested"]);
            break;
        case "free2grow":
            if (data.free2grow?.referenceId) {
                rows.push(["Reference ID", data.free2grow.referenceId]);
            }
            break;
    }
    return rows;
}

export const IntegrationDetailsDialog: FC<IntegrationDetailsDialogProps> = ({ integrationId, data, onClose }) => {
    const catalog = integrationId ? INTEGRATION_CATALOG[integrationId] : undefined;

    return (
        <Dialog open={!!integrationId} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="integration-details-title">
            {integrationId && (
                <>
                    <DialogTitle id="integration-details-title" component="h2">
                        {catalog?.name || integrationId}
                    </DialogTitle>
                    <DialogContent>
                        {catalog && (
                            <>
                                <p className={style.detailsSummary}>{catalog.summary}</p>
                                <ul className={style.capabilities}>
                                    {catalog.capabilities.map((capability) => (
                                        <li key={capability}>{capability}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                        <dl className={style.detailsList}>
                            {integrationDetails(integrationId, data).map(([label, value]) => (
                                <div key={label} data-detail>
                                    <dt>{label}</dt>
                                    <dd>{value}</dd>
                                </div>
                            ))}
                        </dl>
                    </DialogContent>
                    <DialogActions>
                        <button type="button" className={style.detailsButton} onClick={onClose}>
                            Close
                        </button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};
